import { useState } from 'react';
import { Card, Group, Stack, Text, Button, Skeleton, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCreditCard, IconEdit, IconAlertCircle } from '@tabler/icons-react';
import { usePaymentMethod } from '../../hooks/useBilling';
import { useUpdatePaymentMethod } from '../../hooks/useBillingMutations';
import { PaymentMethodForm } from './PaymentMethodForm';
import type { PaymentMethodFormValues } from '../../schemas/billingSchemas';
import type { IBillingAccountViewData } from '@/lib/types/Billing';
import { CountryCodes } from '@lib2/model/form/CountryCodes';

const CARD_TYPE_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  disc: 'Discover',
};

export function PaymentMethodTab() {
  const { paymentMethod, isLoading, isError } = usePaymentMethod();
  const updateMutation = useUpdatePaymentMethod();
  const [isEditing, setIsEditing] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  if (isLoading) {
    return <Skeleton height={200} radius="md" />;
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        Failed to load payment method. Please try again later.
      </Alert>
    );
  }

  if (!paymentMethod) {
    return (
      <Text c="dimmed" size="sm" ta="center" py="xl">
        No payment method on file.
      </Text>
    );
  }

  async function handleSubmit(values: PaymentMethodFormValues) {
    if (!paymentMethod) return;
    setAuthError(null);

    const updatedData: IBillingAccountViewData = {
      ...paymentMethod,
      fullName: values.fullName,
      orgName: values.orgName || null,
      address1: values.address1,
      city: values.city,
      region: values.region,
      postalCode: values.postalCode,
      countryIso2: values.countryIso2,
      cardDetails: {
        cardNum: values.cardNum,
        cardLastFour: values.cardNum.slice(-4),
        cardType: paymentMethod.cardDetails?.cardType ?? 'visa',
        cardMonth: values.cardMonth,
        cardYear: values.cardYear,
        cvv: values.cvv,
      },
    };

    try {
      const result = await updateMutation.mutateAsync({
        billingAccountId: paymentMethod.billingAccountId,
        data: updatedData,
      });

      if (result.authResult && result.authResult.responseCodeName !== 'approved') {
        setAuthError(result.authResult.reason || 'Payment was declined. Please check your card details.');
        return;
      }

      setIsEditing(false);
      notifications.show({ title: 'Saved', message: 'Payment method updated.', color: 'green' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update payment method.',
        color: 'red',
      });
    }
  }

  if (isEditing) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text fw={600} size="lg" mb="md">Edit Payment Method</Text>
        <PaymentMethodForm
          paymentMethod={paymentMethod}
          onSubmit={handleSubmit}
          onCancel={() => { setIsEditing(false); setAuthError(null); }}
          isPending={updateMutation.isPending}
          authError={authError}
        />
      </Card>
    );
  }

  const card = paymentMethod.cardDetails;
  const countryLabel = CountryCodes.getLabel(paymentMethod.countryIso2) ?? paymentMethod.countryIso2;

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text fw={600} size="lg">Payment Method</Text>
        <Button
          leftSection={<IconEdit size={16} />}
          variant="light"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          Edit Payment Method
        </Button>
      </Group>

      <Stack gap="sm">
        {paymentMethod.orgName && (
          <InfoRow label="Organization" value={paymentMethod.orgName} />
        )}
        <InfoRow label="Name" value={paymentMethod.fullName} />
        <InfoRow
          label="Address"
          value={[
            paymentMethod.address1,
            `${paymentMethod.city}, ${paymentMethod.region} ${paymentMethod.postalCode}`,
            countryLabel,
          ].join('\n')}
          multiline
        />
        {card && (
          <InfoRow
            label="Card on File"
            value={`${CARD_TYPE_LABELS[card.cardType] ?? card.cardType} •••• ${card.cardLastFour}`}
            icon={<IconCreditCard size={16} />}
          />
        )}
      </Stack>
    </Card>
  );
}

function InfoRow({
  label,
  value,
  icon,
  multiline,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  multiline?: boolean;
}) {
  return (
    <Group gap="md" align="flex-start">
      <Text size="sm" c="dimmed" w={120} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Group gap="xs">
        {icon}
        <Text size="sm" fw={500} style={multiline ? { whiteSpace: 'pre-line' } : undefined}>
          {value}
        </Text>
      </Group>
    </Group>
  );
}
